import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Btn, Input, Select, inputCls, labelCls } from "./AdminUI";
import { UserPlus, Trash2, Shield, X } from "lucide-react";

const ROLES = ["super_admin", "content_admin", "marketing_user"];
const ROLE_LABEL = { super_admin: "Super Admin", content_admin: "Content Admin", marketing_user: "Marketing User" };

export default function UsersAdmin() {
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [perms, setPerms] = useState({});
  const [showNew, setShowNew] = useState(false);
  const [nu, setNu] = useState({ email: "", name: "", password: "", role: "content_admin" });
  const [err, setErr] = useState("");

  const load = () => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/audit-logs").then((r) => setLogs(r.data)).catch(() => {});
    api.get("/users/roles").then((r) => setPerms(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setErr("");
    if (!nu.email || !nu.password) { setErr("Email and password are required"); return; }
    try { await api.post("/users", nu); setShowNew(false); setNu({ email: "", name: "", password: "", role: "content_admin" }); load(); }
    catch (e) { setErr(e?.response?.data?.detail || "Could not create user"); }
  };
  const upd = async (id, patch) => { try { await api.put(`/users/${id}`, patch); load(); } catch (e) { alert(e?.response?.data?.detail || "Update failed"); } };
  const del = async (id) => { if (!window.confirm("Delete this user?")) return; try { await api.delete(`/users/${id}`); load(); } catch (e) { alert(e?.response?.data?.detail || "Delete failed"); } };

  return (
    <div className="max-w-5xl" data-testid="users-admin">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-ink">Users & Permissions</h1>
          <p className="text-sm text-muted">Manage admin accounts and roles. Permissions are enforced on the server for every action.</p>
        </div>
        <Btn onClick={() => setShowNew(true)} data-testid="add-user"><UserPlus size={16} /> Add User</Btn>
      </div>

      {showNew && (
        <div className="rounded-xl2 border border-line bg-white p-5 mb-6" data-testid="new-user-form">
          <div className="flex items-center justify-between mb-3"><h3 className="font-bold text-ink">New Admin User</h3><button onClick={() => setShowNew(false)}><X size={18} /></button></div>
          <div className="grid md:grid-cols-2 gap-3">
            <Input label="Name" value={nu.name} onChange={(v) => setNu({ ...nu, name: v })} />
            <Input label="Email *" value={nu.email} onChange={(v) => setNu({ ...nu, email: v })} />
            <Input label="Password *" value={nu.password} onChange={(v) => setNu({ ...nu, password: v })} />
            <Select label="Role" value={nu.role} onChange={(v) => setNu({ ...nu, role: v })} options={ROLES} />
          </div>
          {err && <p className="text-sm text-red-500 mt-2">{err}</p>}
          <div className="mt-4"><Btn onClick={create} data-testid="save-user">Create User</Btn></div>
        </div>
      )}

      <div className="rounded-xl2 border border-line bg-white overflow-hidden mb-8">
        <table className="w-full text-sm">
          <thead className="bg-sand text-left text-xs uppercase text-muted"><tr><th className="p-3">User</th><th className="p-3">Role</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-line" data-testid={`user-row-${u.email}`}>
                <td className="p-3"><div className="font-semibold text-ink">{u.name || "—"}</div><div className="text-xs text-muted">{u.email}</div></td>
                <td className="p-3">
                  <select className={inputCls + " max-w-[160px]"} value={u.role} onChange={(e) => upd(u.id, { role: e.target.value })} data-testid={`role-${u.email}`}>
                    {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                  </select>
                </td>
                <td className="p-3">
                  <button onClick={() => upd(u.id, { active: u.active === false })} className={`text-xs font-bold px-2 py-1 rounded-full border ${u.active === false ? "bg-red-50 text-red-600 border-red-200" : "bg-green-50 text-green-700 border-green-200"}`} data-testid={`status-${u.email}`}>
                    {u.active === false ? "Inactive" : "Active"}
                  </button>
                </td>
                <td className="p-3 text-right"><button onClick={() => del(u.id)} className="text-red-500 p-1" data-testid={`delete-${u.email}`}><Trash2 size={16} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl2 border border-line bg-white p-5">
          <h3 className="font-bold text-ink mb-3 flex items-center gap-2"><Shield size={16} className="text-gold" /> Role Permissions</h3>
          <div className="space-y-3">
            {Object.entries(perms).map(([role, list]) => (
              <div key={role}>
                <div className="font-semibold text-ink text-sm">{ROLE_LABEL[role] || role}</div>
                <div className="text-xs text-muted mt-1">{list.includes("all") ? "Full access to all modules" : list.join(", ")}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-xl2 border border-line bg-white p-5">
          <h3 className="font-bold text-ink mb-3">Recent Activity Log</h3>
          <div className="space-y-2 max-h-80 overflow-auto" data-testid="audit-log">
            {logs.length === 0 && <p className="text-sm text-muted">No activity recorded yet.</p>}
            {logs.map((l) => (
              <div key={l.id} className="text-xs border-b border-line pb-2">
                <span className="font-semibold text-ink">{l.action}</span> {l.item && <span className="text-muted">· {l.item}</span>}
                <div className="text-muted">{l.user} · {new Date(l.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
